import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Edit, Trash2, Loader2, Car, Calendar, Gauge, Fuel, Settings, ChevronLeft, ChevronRight, DollarSign, FileCheck, Globe, Calculator, ClipboardList, FolderOpen, Truck, TrendingUp, FileText, X, ZoomIn } from 'lucide-react';
import { api } from '../../services/api';
import { useNotification } from '../../contexts/NotificationContext';
import { PageHeader, ConfirmDialog } from '../../components/ui';
import { Badge, BadgeTone, buttonClass } from '../../design-system';
import { CustosVeiculoModal, PendenciasVeiculoModal, VistoriasVeiculoModal, PublicacoesVeiculoModal, FinanceiroVeiculoModal, ArquivosVeiculoModal, DespachanteVeiculoModal, EntradaVeiculoModal, FipeVeiculoModal, NfeVeiculoModal } from '../../components/veiculos';
import { formatCurrency, formatKm, formatPlaca, formatDate } from '../../utils/format';
import type { CustoVeiculoResumo, PendenciaVeiculoResumo, VistoriaVeiculoResumo } from '../../types';

interface VeiculoDetail {
  id: number;
  lojaId: number;
  marcaId: number;
  marcaNome: string;
  modeloId: number;
  modeloNome: string;
  versao: string | null;
  anoFabricacao: number;
  anoModelo: number;
  corId: number | null;
  corNome: string | null;
  cor: string | null; // Legado
  placa: string | null;
  renavam: string | null;
  chassi: string | null;
  km: number | null;
  combustivel: string | null;
  cambio: string | null;
  portas: number | null;
  valorCompra: number | null;
  valorVenda: number;
  valorFipe: number | null;
  status: string;
  dataCompra: string | null;
  dataVenda: string | null;
  dataEntrada: string | null;
  fornecedorId: number | null;
  fornecedorNome: string | null;
  observacoes: string | null;
  dataCadastro: string;
  fotos: { id: number; url: string; principal: boolean }[];
}

const statusTones: Record<string, BadgeTone> = {
  DISPONIVEL: 'success',
  RESERVADO: 'warning',
  VENDIDO: 'info',
  CONSIGNADO: 'primary',
};

const gestaoCardClass =
  'group rounded-2xl bg-white border border-slate-200/70 shadow-[0_1px_2px_rgba(15,23,42,0.04),0_1px_3px_rgba(15,23,42,0.04)] p-4 text-left transition-all hover:-translate-y-0.5 hover:border-slate-300 hover:shadow-[0_10px_30px_-12px_rgba(15,23,42,0.18)]';
const gestaoIconClass =
  'grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-slate-50 text-slate-400 ring-1 ring-slate-100 transition-colors group-hover:bg-primary-50 group-hover:text-primary-600 group-hover:ring-primary-100';

const statusLabels: Record<string, string> = {
  DISPONIVEL: 'Disponível',
  RESERVADO: 'Reservado',
  VENDIDO: 'Vendido',
  CONSIGNADO: 'Consignado',
};

export function VeiculoDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { showSuccess, showError } = useNotification();

  const [veiculo, setVeiculo] = useState<VeiculoDetail | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [currentFotoIndex, setCurrentFotoIndex] = useState(0);
  const [showCustosModal, setShowCustosModal] = useState(false);
  const [custosResumo, setCustosResumo] = useState<CustoVeiculoResumo | null>(null);
  const [showPendenciasModal, setShowPendenciasModal] = useState(false);
  const [pendenciasResumo, setPendenciasResumo] = useState<PendenciaVeiculoResumo | null>(null);
  const [showVistoriasModal, setShowVistoriasModal] = useState(false);
  const [vistoriasResumo, setVistoriasResumo] = useState<VistoriaVeiculoResumo | null>(null);
  const [showPublicacoesModal, setShowPublicacoesModal] = useState(false);
  const [showFinanceiroModal, setShowFinanceiroModal] = useState(false);
  const [showArquivosModal, setShowArquivosModal] = useState(false);
  const [showDespachanteModal, setShowDespachanteModal] = useState(false);
  const [showEntradaModal, setShowEntradaModal] = useState(false);
  const [showFipeModal, setShowFipeModal] = useState(false);
  const [showNfeModal, setShowNfeModal] = useState(false);
  const [showImageLightbox, setShowImageLightbox] = useState(false);

  useEffect(() => {
    if (!id) return;
    const controller = new AbortController();
    setIsLoading(true);

    // Carregar veículo primeiro (obrigatório)
    api.get<VeiculoDetail>(`/veiculos/${id}`, { signal: controller.signal })
      .then((veiculoRes) => {
        setVeiculo(veiculoRes.data);

        // Carregar dados complementares em paralelo (opcionais)
        Promise.all([
          api.get<CustoVeiculoResumo>(`/veiculos/${id}/custos/resumo`, { signal: controller.signal }).catch(() => ({ data: null })),
          api.get<PendenciaVeiculoResumo>(`/veiculos/${id}/pendencias/resumo`, { signal: controller.signal }).catch(() => ({ data: null })),
          api.get<VistoriaVeiculoResumo>(`/veiculos/${id}/vistorias/resumo`, { signal: controller.signal }).catch(() => ({ data: null })),
        ]).then(([custosRes, pendenciasRes, vistoriasRes]) => {
          setCustosResumo(custosRes.data);
          setPendenciasResumo(pendenciasRes.data);
          setVistoriasResumo(vistoriasRes.data);
        });
      })
      .catch((error) => {
        if (error.name !== 'CanceledError') {
          showError('Erro ao carregar veículo');
        }
      })
      .finally(() => setIsLoading(false));

    return () => controller.abort();
  }, [id, showError]);

  const reloadCustosResumo = async () => {
    if (!id) return;
    try {
      const response = await api.get<CustoVeiculoResumo>(`/veiculos/${id}/custos/resumo`);
      setCustosResumo(response.data);
    } catch {
      // Silently fail
    }
  };

  const handleDelete = async () => {
    if (!id) return;
    setIsDeleting(true);
    try {
      await api.delete(`/veiculos/${id}`);
      showSuccess('Veículo excluído com sucesso');
      navigate('/veiculos');
    } catch {
      showError('Erro ao excluir veículo');
    } finally {
      setIsDeleting(false);
      setShowDeleteDialog(false);
    }
  };

  const nextFoto = () => {
    if (!veiculo?.fotos.length) return;
    setCurrentFotoIndex((prev) => (prev + 1) % veiculo.fotos.length);
  };

  const prevFoto = () => {
    if (!veiculo?.fotos.length) return;
    setCurrentFotoIndex((prev) => (prev - 1 + veiculo.fotos.length) % veiculo.fotos.length);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    );
  }

  if (!veiculo) {
    return (
      <div className="text-center py-12">
        <p className="text-slate-500">Veículo não encontrado</p>
      </div>
    );
  }

  const lucro = veiculo.valorCompra
    ? veiculo.valorVenda - veiculo.valorCompra
    : null;
  const margemPercentual = lucro && veiculo.valorCompra
    ? (lucro / veiculo.valorCompra) * 100
    : null;

  return (
    <div>
      <PageHeader
        title={`${veiculo.marcaNome} ${veiculo.modeloNome}`}
        subtitle={veiculo.versao ? `${veiculo.versao} • ${veiculo.anoFabricacao}/${veiculo.anoModelo}` : `${veiculo.anoFabricacao}/${veiculo.anoModelo}`}
        showBack
        backTo="/veiculos"
      >
        <div className="flex gap-2">
          <button
            onClick={() => navigate(`/veiculos/${id}/editar`)}
            className={buttonClass('secondary')}
          >
            <Edit className="w-4 h-4" />
            Editar
          </button>
          <button
            onClick={() => setShowDeleteDialog(true)}
            className={buttonClass('danger')}
          >
            <Trash2 className="w-4 h-4" />
            Excluir
          </button>
        </div>
      </PageHeader>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* Fotos - Reduzido para ocupar 3/5 em vez de 2/3 */}
        <div className="lg:col-span-3">
          <div className="rounded-2xl bg-white border border-slate-200/70 shadow-[0_1px_2px_rgba(15,23,42,0.04),0_1px_3px_rgba(15,23,42,0.04)] overflow-hidden">
            {veiculo.fotos.length > 0 ? (
              <div
                className="relative aspect-[16/10] bg-slate-100 cursor-pointer group"
                onClick={() => setShowImageLightbox(true)}
              >
                <img
                  src={veiculo.fotos[currentFotoIndex].url}
                  alt={`${veiculo.marcaNome} ${veiculo.modeloNome}`}
                  className="w-full h-full object-contain"
                />
                {/* Overlay com ícone de zoom */}
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                  <div className="opacity-0 group-hover:opacity-100 transition-opacity bg-black/50 p-3 rounded-full">
                    <ZoomIn className="w-6 h-6 text-white" />
                  </div>
                </div>
                {veiculo.fotos.length > 1 && (
                  <>
                    <button
                      onClick={(e) => { e.stopPropagation(); prevFoto(); }}
                      className="absolute left-2 top-1/2 -translate-y-1/2 p-2 bg-black/50 text-white rounded-full hover:bg-black/70 transition-colors"
                    >
                      <ChevronLeft className="w-5 h-5" />
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); nextFoto(); }}
                      className="absolute right-2 top-1/2 -translate-y-1/2 p-2 bg-black/50 text-white rounded-full hover:bg-black/70 transition-colors"
                    >
                      <ChevronRight className="w-5 h-5" />
                    </button>
                    <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
                      {veiculo.fotos.map((_, index) => (
                        <button
                          key={index}
                          onClick={(e) => { e.stopPropagation(); setCurrentFotoIndex(index); }}
                          className={`w-2 h-2 rounded-full transition-colors ${
                            index === currentFotoIndex ? 'bg-white' : 'bg-white/50'
                          }`}
                        />
                      ))}
                    </div>
                  </>
                )}
              </div>
            ) : (
              <div className="aspect-[16/10] flex items-center justify-center bg-slate-100">
                <Car className="w-16 h-16 text-slate-300" />
              </div>
            )}

            {/* Thumbnails - Menores */}
            {veiculo.fotos.length > 1 && (
              <div className="p-3 border-t border-slate-100">
                <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-thin scrollbar-thumb-slate-300 scrollbar-track-slate-100">
                  {veiculo.fotos.map((foto, index) => (
                    <button
                      key={foto.id}
                      onClick={() => setCurrentFotoIndex(index)}
                      className={`flex-shrink-0 w-14 h-14 md:w-16 md:h-16 rounded-lg overflow-hidden border-2 transition-all hover:opacity-80 ${
                        index === currentFotoIndex ? 'border-primary-500 ring-2 ring-primary-200' : 'border-slate-200'
                      }`}
                    >
                      <img src={foto.url} alt="" className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Info Card - Agora ocupa 2/5 */}
        <div className="lg:col-span-2 space-y-4">
          {/* Status e Preço */}
          <div className="rounded-2xl bg-white border border-slate-200/70 shadow-[0_1px_2px_rgba(15,23,42,0.04),0_1px_3px_rgba(15,23,42,0.04)] p-5">
            <div className="flex items-center justify-between mb-3">
              <Badge tone={statusTones[veiculo.status] || 'neutral'}>
                {statusLabels[veiculo.status] || veiculo.status}
              </Badge>
              {veiculo.placa && (
                <span className="font-mono text-lg font-bold text-slate-700">
                  {formatPlaca(veiculo.placa)}
                </span>
              )}
            </div>

            <div className="text-2xl font-bold tabular-nums text-slate-900 mb-3">
              {formatCurrency(veiculo.valorVenda)}
            </div>

            {lucro !== null && (
              <div className="grid grid-cols-2 gap-4 pt-3 border-t border-slate-100">
                <div>
                  <p className="text-xs text-slate-500">Lucro</p>
                  <p className={`font-semibold tabular-nums ${lucro >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                    {formatCurrency(lucro)}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Margem</p>
                  <p className={`font-semibold tabular-nums ${margemPercentual && margemPercentual >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                    {margemPercentual?.toFixed(1)}%
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Características e Valores lado a lado */}
          <div className="grid grid-cols-2 gap-3">
            {/* Características */}
            <div className="rounded-2xl bg-white border border-slate-200/70 shadow-[0_1px_2px_rgba(15,23,42,0.04),0_1px_3px_rgba(15,23,42,0.04)] p-4">
              <h3 className="font-bold tracking-tight text-slate-900 mb-3 text-sm">Características</h3>
              <div className="space-y-2 text-sm">
                <div className="flex items-center gap-2 text-slate-600">
                  <Calendar className="w-4 h-4 text-slate-400 flex-shrink-0" />
                  <span>{veiculo.anoFabricacao}/{veiculo.anoModelo}</span>
                </div>
                {veiculo.km !== null && (
                  <div className="flex items-center gap-2 text-slate-600">
                    <Gauge className="w-4 h-4 text-slate-400 flex-shrink-0" />
                    <span>{formatKm(veiculo.km)}</span>
                  </div>
                )}
                {veiculo.combustivel && (
                  <div className="flex items-center gap-2 text-slate-600">
                    <Fuel className="w-4 h-4 text-slate-400 flex-shrink-0" />
                    <span>{veiculo.combustivel}</span>
                  </div>
                )}
                {veiculo.cambio && (
                  <div className="flex items-center gap-2 text-slate-600">
                    <Settings className="w-4 h-4 text-slate-400 flex-shrink-0" />
                    <span>{veiculo.cambio}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Valores */}
            <div className="rounded-2xl bg-white border border-slate-200/70 shadow-[0_1px_2px_rgba(15,23,42,0.04),0_1px_3px_rgba(15,23,42,0.04)] p-4">
              <h3 className="font-bold tracking-tight text-slate-900 mb-3 text-sm">Valores</h3>
              <div className="space-y-2 text-sm">
                {veiculo.valorCompra && (
                  <div className="flex justify-between">
                    <span className="text-slate-500">Compra</span>
                    <span className="font-medium tabular-nums text-slate-900">{formatCurrency(veiculo.valorCompra)}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-slate-500">Venda</span>
                  <span className="font-bold tabular-nums text-slate-900">{formatCurrency(veiculo.valorVenda)}</span>
                </div>
                {veiculo.valorFipe && (
                  <div className="flex justify-between">
                    <span className="text-slate-500">FIPE</span>
                    <span className="font-medium tabular-nums text-slate-900">{formatCurrency(veiculo.valorFipe)}</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Datas */}
          <div className="rounded-2xl bg-white border border-slate-200/70 shadow-[0_1px_2px_rgba(15,23,42,0.04),0_1px_3px_rgba(15,23,42,0.04)] p-4">
            <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
              {veiculo.dataEntrada && (
                <>
                  <span className="text-slate-500">Entrada</span>
                  <span className="text-right tabular-nums text-slate-900">{formatDate(veiculo.dataEntrada)}</span>
                </>
              )}
              {veiculo.dataCompra && (
                <>
                  <span className="text-slate-500">Compra</span>
                  <span className="text-right tabular-nums text-slate-900">{formatDate(veiculo.dataCompra)}</span>
                </>
              )}
              {veiculo.dataVenda && (
                <>
                  <span className="text-slate-500">Venda</span>
                  <span className="text-right tabular-nums text-slate-900">{formatDate(veiculo.dataVenda)}</span>
                </>
              )}
            </div>
          </div>

          {/* Observações */}
          {veiculo.observacoes && (
            <div className="rounded-2xl bg-white border border-slate-200/70 shadow-[0_1px_2px_rgba(15,23,42,0.04),0_1px_3px_rgba(15,23,42,0.04)] p-4">
              <h3 className="font-bold tracking-tight text-slate-900 mb-2 text-sm">Observações</h3>
              <p className="text-sm text-slate-600 whitespace-pre-wrap">{veiculo.observacoes}</p>
            </div>
          )}
        </div>
      </div>

      {/* Seção de Gestão - Grid de Cards Compactos */}
      <div className="mt-6">
        <h3 className="text-lg font-bold tracking-tight text-slate-900 mb-4">Gestão do Veículo</h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          {/* Custos */}
          <button
            onClick={() => setShowCustosModal(true)}
            className={gestaoCardClass}
          >
            <div className="flex items-center gap-3 mb-2">
              <div className={gestaoIconClass}>
                <DollarSign className="w-5 h-5" />
              </div>
              <span className="font-semibold text-slate-900">Custos</span>
            </div>
            <div className="text-sm">
              <span className="font-bold tabular-nums text-slate-900">
                {custosResumo ? formatCurrency(custosResumo.totalCustos) : 'R$ 0'}
              </span>
              {custosResumo && custosResumo.totalPendente > 0 && (
                <span className="text-amber-600 text-xs ml-1">
                  ({custosResumo.quantidadeLancamentos})
                </span>
              )}
            </div>
          </button>

          {/* Pendências */}
          <button
            onClick={() => setShowPendenciasModal(true)}
            className={gestaoCardClass}
          >
            <div className="flex items-center gap-3 mb-2">
              <div className={gestaoIconClass}>
                <ClipboardList className="w-5 h-5" />
              </div>
              <span className="font-semibold text-slate-900">Pendências</span>
            </div>
            <div className="text-sm">
              {pendenciasResumo && pendenciasResumo.pendentes > 0 ? (
                <span className="text-amber-600 font-semibold">{pendenciasResumo.pendentes} pendente(s)</span>
              ) : (
                <span className="text-emerald-600">Nenhuma</span>
              )}
            </div>
          </button>

          {/* Vistorias */}
          <button
            onClick={() => setShowVistoriasModal(true)}
            className={gestaoCardClass}
          >
            <div className="flex items-center gap-3 mb-2">
              <div className={gestaoIconClass}>
                <FileCheck className="w-5 h-5" />
              </div>
              <span className="font-semibold text-slate-900">Vistorias</span>
            </div>
            <div className="text-sm">
              {vistoriasResumo ? (
                <span className="font-semibold text-slate-900">{vistoriasResumo.totalVistorias} vistoria(s)</span>
              ) : (
                <span className="text-slate-500">Nenhuma</span>
              )}
            </div>
          </button>

          {/* Publicações */}
          <button
            onClick={() => setShowPublicacoesModal(true)}
            className={gestaoCardClass}
          >
            <div className="flex items-center gap-3 mb-2">
              <div className={gestaoIconClass}>
                <Globe className="w-5 h-5" />
              </div>
              <span className="font-semibold text-slate-900">Publicações</span>
            </div>
            <div className="text-sm text-slate-500">Portais web</div>
          </button>

          {/* Financeiro */}
          <button
            onClick={() => setShowFinanceiroModal(true)}
            className={gestaoCardClass}
          >
            <div className="flex items-center gap-3 mb-2">
              <div className={gestaoIconClass}>
                <Calculator className="w-5 h-5" />
              </div>
              <span className="font-semibold text-slate-900">Financeiro</span>
            </div>
            <div className="text-sm text-slate-500">Análise completa</div>
          </button>

          {/* Arquivos */}
          <button
            onClick={() => setShowArquivosModal(true)}
            className={gestaoCardClass}
          >
            <div className="flex items-center gap-3 mb-2">
              <div className={gestaoIconClass}>
                <FolderOpen className="w-5 h-5" />
              </div>
              <span className="font-semibold text-slate-900">Arquivos</span>
            </div>
            <div className="text-sm text-slate-500">Documentos</div>
          </button>

          {/* Despachante */}
          <button
            onClick={() => setShowDespachanteModal(true)}
            className={gestaoCardClass}
          >
            <div className="flex items-center gap-3 mb-2">
              <div className={gestaoIconClass}>
                <Truck className="w-5 h-5" />
              </div>
              <span className="font-semibold text-slate-900">Despachante</span>
            </div>
            <div className="text-sm text-slate-500">Transferência</div>
          </button>

          {/* Entrada */}
          <button
            onClick={() => setShowEntradaModal(true)}
            className={gestaoCardClass}
          >
            <div className="flex items-center gap-3 mb-2">
              <div className={gestaoIconClass}>
                <Car className="w-5 h-5" />
              </div>
              <span className="font-semibold text-slate-900">Entrada</span>
            </div>
            <div className="text-sm text-slate-500">Aquisição</div>
          </button>

          {/* FIPE */}
          <button
            onClick={() => setShowFipeModal(true)}
            className={gestaoCardClass}
          >
            <div className="flex items-center gap-3 mb-2">
              <div className={gestaoIconClass}>
                <TrendingUp className="w-5 h-5" />
              </div>
              <span className="font-semibold text-slate-900">FIPE</span>
            </div>
            <div className="text-sm text-slate-500">Histórico</div>
          </button>

          {/* NFe */}
          <button
            onClick={() => setShowNfeModal(true)}
            className={gestaoCardClass}
          >
            <div className="flex items-center gap-3 mb-2">
              <div className={gestaoIconClass}>
                <FileText className="w-5 h-5" />
              </div>
              <span className="font-semibold text-slate-900">NFe</span>
            </div>
            <div className="text-sm text-slate-500">Notas fiscais</div>
          </button>
        </div>
      </div>

      {/* Modal Lightbox para visualização de imagens em tela cheia */}
      {showImageLightbox && veiculo.fotos.length > 0 && (
        <div
          className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center"
          onClick={() => setShowImageLightbox(false)}
        >
          {/* Botão fechar */}
          <button
            onClick={() => setShowImageLightbox(false)}
            className="absolute top-4 right-4 p-2 text-white/70 hover:text-white transition-colors z-10"
          >
            <X className="w-8 h-8" />
          </button>

          {/* Contador de fotos */}
          <div className="absolute top-4 left-4 text-white/70 text-sm">
            {currentFotoIndex + 1} / {veiculo.fotos.length}
          </div>

          {/* Imagem principal */}
          <div className="relative w-full h-full flex items-center justify-center p-4 md:p-12">
            <img
              src={veiculo.fotos[currentFotoIndex].url}
              alt={`${veiculo.marcaNome} ${veiculo.modeloNome}`}
              className="max-w-full max-h-full object-contain"
              onClick={(e) => e.stopPropagation()}
            />

            {/* Navegação */}
            {veiculo.fotos.length > 1 && (
              <>
                <button
                  onClick={(e) => { e.stopPropagation(); prevFoto(); }}
                  className="absolute left-4 top-1/2 -translate-y-1/2 p-3 bg-white/10 hover:bg-white/20 text-white rounded-full transition-colors"
                >
                  <ChevronLeft className="w-8 h-8" />
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); nextFoto(); }}
                  className="absolute right-4 top-1/2 -translate-y-1/2 p-3 bg-white/10 hover:bg-white/20 text-white rounded-full transition-colors"
                >
                  <ChevronRight className="w-8 h-8" />
                </button>
              </>
            )}
          </div>

          {/* Thumbnails no lightbox */}
          {veiculo.fotos.length > 1 && (
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 max-w-[90vw] overflow-x-auto pb-2">
              {veiculo.fotos.map((foto, index) => (
                <button
                  key={foto.id}
                  onClick={(e) => { e.stopPropagation(); setCurrentFotoIndex(index); }}
                  className={`flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 transition-all ${
                    index === currentFotoIndex ? 'border-white ring-2 ring-white/50' : 'border-transparent opacity-50 hover:opacity-80'
                  }`}
                >
                  <img src={foto.url} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      <ConfirmDialog
        isOpen={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
        onConfirm={handleDelete}
        type="danger"
        title="Excluir Veículo"
        message={`Tem certeza que deseja excluir o veículo ${veiculo.marcaNome} ${veiculo.modeloNome}? Esta ação não pode ser desfeita.`}
        confirmText="Excluir"
        isLoading={isDeleting}
      />

      <CustosVeiculoModal
        isOpen={showCustosModal}
        onClose={() => {
          setShowCustosModal(false);
          reloadCustosResumo();
        }}
        veiculoId={veiculo.id}
        veiculoNome={`${veiculo.marcaNome} ${veiculo.modeloNome}`}
      />

      <PendenciasVeiculoModal
        isOpen={showPendenciasModal}
        onClose={() => setShowPendenciasModal(false)}
        veiculoId={veiculo.id}
        veiculoNome={`${veiculo.marcaNome} ${veiculo.modeloNome}`}
      />

      <VistoriasVeiculoModal
        isOpen={showVistoriasModal}
        onClose={() => setShowVistoriasModal(false)}
        veiculoId={veiculo.id}
        veiculoNome={`${veiculo.marcaNome} ${veiculo.modeloNome}`}
      />

      <PublicacoesVeiculoModal
        isOpen={showPublicacoesModal}
        onClose={() => setShowPublicacoesModal(false)}
        veiculoId={veiculo.id}
        veiculoNome={`${veiculo.marcaNome} ${veiculo.modeloNome}`}
      />

      <FinanceiroVeiculoModal
        isOpen={showFinanceiroModal}
        onClose={() => setShowFinanceiroModal(false)}
        veiculoId={veiculo.id}
        veiculoNome={`${veiculo.marcaNome} ${veiculo.modeloNome}`}
      />

      <ArquivosVeiculoModal
        isOpen={showArquivosModal}
        onClose={() => setShowArquivosModal(false)}
        veiculoId={veiculo.id}
        veiculoNome={`${veiculo.marcaNome} ${veiculo.modeloNome}`}
      />

      <DespachanteVeiculoModal
        isOpen={showDespachanteModal}
        onClose={() => setShowDespachanteModal(false)}
        veiculoId={veiculo.id}
        veiculoNome={`${veiculo.marcaNome} ${veiculo.modeloNome}`}
      />

      <EntradaVeiculoModal
        isOpen={showEntradaModal}
        onClose={() => setShowEntradaModal(false)}
        veiculoId={veiculo.id}
        veiculoNome={`${veiculo.marcaNome} ${veiculo.modeloNome}`}
      />

      <FipeVeiculoModal
        isOpen={showFipeModal}
        onClose={() => setShowFipeModal(false)}
        veiculoId={veiculo.id}
        veiculoNome={`${veiculo.marcaNome} ${veiculo.modeloNome}`}
      />

      <NfeVeiculoModal
        isOpen={showNfeModal}
        onClose={() => setShowNfeModal(false)}
        veiculoId={veiculo.id}
        veiculoNome={`${veiculo.marcaNome} ${veiculo.modeloNome}`}
      />
    </div>
  );
}
